import React from "react";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { useTranslation } from "react-i18next";
import {
  FiUsers as Users,
  FiBook as Library,
  FiBell as Bell,
  FiSettings as Settings,
  FiChevronRight as ChevronRight,
  FiPlus as Plus,
  
} from "react-icons/fi";
import { FaUserCog } from "react-icons/fa";
import { AiOutlineEdit } from "react-icons/ai";
import { FiTrash } from "react-icons/fi";
import { ITeam } from "../../interfaces/team.interface";
import { IUser } from "../../../../context/interfaces/User";
import { defineRight } from "../ManageUserTab/helpers/defineRight";

// backward-compatible alias: some files/components may still reference `Pencil`
const Pencil = AiOutlineEdit;

const cn = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(" ");

type SidebarTab = "manage_users" | "prompt_library" | "notifications" | "user_settings";

const menuItems: Array<{ id: SidebarTab; label: string; icon: any }> = [
  { id: "manage_users", label: "Manage Users", icon: Users },
  { id: "prompt_library", label: "Prompt Library", icon: Library },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "user_settings", label: "User Settings", icon: Settings },
];

interface AdminSidebarProps {
  activeTab: SidebarTab;
  onTabChange: (tab: SidebarTab) => void;

  user: IUser;

  teams: ITeam[];
  selectedTeam: ITeam | null;
  onSelectTeam: (team: ITeam | null) => void;

  selectedTeamRegulators?: Array<{ id: number | string; name: string }>;

  onCreateTeam: () => void;
  onEditTeam: (team: ITeam) => void;
  onDeleteTeam: (team: ITeam) => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  activeTab,
  onTabChange,
  user,
  teams,
  selectedTeam,
  onSelectTeam,
  selectedTeamRegulators,
  onCreateTeam,
  onEditTeam,
  onDeleteTeam,
}) => {
  const title = user.is_superuser ? "Superadmin Panel" : "Admin Panel";
  const subtitle = "Manage your organization";
  const { t } = useTranslation();

  return (
    <aside className="w-72 h-[calc(100vh-48px)] border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col">
      {/* Sidebar Header */}
      <div className="p-6 border-b border-slate-200 dark:border-slate-700">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {title}
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          {subtitle}
        </p>
      </div>

      {/* IMPORTANT: no overflow here; we want only Teams to scroll */}
      <div className="flex-1 min-h-0 flex flex-col">
        {/* Navigation (no scroll) */}
        <nav className="p-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;

            return (
              <button
                key={item.id}
                onClick={() => onTabChange(item.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-normal transition-colors",
                  isActive
                    ? "bg-del-green/10 text-del-green"
                    : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800",
                )}
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span className="flex-1 text-left">{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4" />}
              </button>
            );
          })}
        </nav>

        {/* Teams Section (header fixed; list scrolls) */}
        <div className="p-4 mt-2 border-t border-slate-200 dark:border-slate-700 flex-1 min-h-0 flex flex-col">
          <div className="flex items-center justify-between mb-3 shrink-0">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Teams
            </h3>

            {user.is_superuser && (
              <button
                onClick={onCreateTeam}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                aria-label="Create team"
              >
                <Plus className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Only THIS part scrolls */}
          <div className="flex-1 min-h-0 overflow-auto pr-1">
            {teams.length === 0 ? (
              <div className="bg-slate-100 dark:bg-slate-800/40 rounded-lg p-6 text-center border border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  No teams found
                </p>
                {user.is_superuser && (
                  <button
                    onClick={onCreateTeam}
                    className="mt-3 inline-flex items-center justify-center h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Team
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                {teams.map((team) => {
                  const isSelected = selectedTeam?.id === team.id;
                  const canEditTeam = user.is_superuser || defineRight(user, team) === "Admin";
                  const showAdminIcon = defineRight(user, team) === "Admin";

                  return (
                    <div key={team.id} className="rounded-lg">
                      <button
                        onClick={() => onSelectTeam(isSelected ? null : team)}
                        className={cn(
                          "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition focus:outline-none focus:ring-0",
                          isSelected
                            ? "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-semibold"
                            : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800",
                        )}
                      >
                        <span
                          className={cn(
                            "flex-1 text-left",
                            isSelected ? "whitespace-normal" : "truncate",
                          )}
                          title={team.name}
                        >
                          {team.name}
                        </span>

                        {showAdminIcon && (
                          <>
                            <FaUserCog className={`w-4 h-4 opacity-70 tooltip-admin-${team.id}`} />
                            <Tooltip
                              anchorSelect={`.tooltip-admin-${team.id}`}
                              place="top"
                              className="text-sm font-normal"
                              content={
                                t
                                  ? t("adminPanel.adminOfTeam", { defaultValue: "You are Admin of this team" })
                                  : "You are Admin of this team"
                              }
                            />
                          </>
                        )}

                        <div className="flex items-center gap-1">
                          {canEditTeam && (
                            <button
                              className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-slate-200/60 dark:hover:bg-slate-700/60"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditTeam(team);
                              }}
                              aria-label="Edit team"
                            >
                              <AiOutlineEdit className="w-4 h-4" />
                            </button>
                          )}

                            {user.is_superuser && (
                            <button
                              className="h-8 w-8 inline-flex items-center justify-center rounded-lg hover:bg-red-500/10"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteTeam(team);
                              }}
                              aria-label="Delete team"
                            >
                                <FiTrash className="w-4 h-4 text-red-600" />
                            </button>
                          )}
                        </div>
                      </button>

                      {isSelected && Array.isArray(selectedTeamRegulators) && selectedTeamRegulators.length > 0 && (
                        <div className="mt-1 ml-2 pl-3 border-l border-slate-200 dark:border-slate-700">
                          <ul className="py-1 space-y-1">
                            {selectedTeamRegulators.map((reg) => (
                              <li
                                key={reg.id}
                                className="text-sm text-slate-600 dark:text-slate-300 whitespace-normal"
                                title={reg.name}
                              >
                                {reg.name}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};